import { allowedForContentsOverride } from '@/components/feedbacks/session-feedback-utils';
import { FormModal, SubmitProps } from '@/components/form-modal';
import { FormCheckbox } from '@/components/forms/form-checkbox';
import { FormCheckboxGroup } from '@/components/forms/form-checkbox-group';
import { FormInputField } from '@/components/forms/form-input-field';
import { FormMultiSelect } from '@/components/forms/form-multi-select';
import { FormTextArea } from '@/components/forms/form-text-area';
import { Button } from '@/components/ui/button';
import { useToast } from '@/components/ui/hooks/use-toast';
import { MultiSelectOption } from '@/components/ui/multi-select';
import { buildNameLikeSelectOptions, multiSelectOptions } from '@/components/ui/multi-select-utils';
import { createLesson, getAssessments, getLessons } from '@/lib/api-calls';
import { textAreaValueToArray } from '@/lib/string-utils';
import { isNotNullish } from '@airreading/common';
import { CreateLessonOptions, optionalFormNumber } from '@airreading/common/data';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import isURL from 'validator/es/lib/isURL';
import { z } from 'zod';

const formSchema = z.object({
  name: z.string(),
  lessonLinkKTo2: z
    .string()
    .optional()
    .refine((val) => !val || isURL(val), { message: 'Invalid URL' }),
  lessonLink3To5: z
    .string()
    .optional()
    .refine((val) => !val || isURL(val), { message: 'Invalid URL' }),
  lessonLink6To8: z
    .string()
    .optional()
    .refine((val) => !val || isURL(val), { message: 'Invalid URL' }),
  handouts: z
    .string()
    .optional()
    .refine((val) => {
      const handoutLinks = textAreaValueToArray(val);
      return handoutLinks.length === 0 || handoutLinks.every((link) => isURL(link));
    }, 'Handout links must be valid URLs'),
  objectives: z.string().optional(),
  assessmentIds: z.array(z.string()),
  selectedAssessmentIdForContentsOverride: z.string().optional(),
  overridenAssessmentContents: z.array(
    z.object({
      useContentsOverride: z.boolean(),
      overridenContents: multiSelectOptions,
    }),
  ),
  minWPM: optionalFormNumber(z.coerce.number().nonnegative().int()),
  maxWPM: optionalFormNumber(z.coerce.number().nonnegative().int()),
  minAccuracyPercent: optionalFormNumber(z.coerce.number().nonnegative()),
  maxAccuracyPercent: optionalFormNumber(z.coerce.number().nonnegative()),
  floridaStandard: z.string().optional(),
  arkansasStandard: z.string().optional(),
});

export const CreateLessons = () => {
  const { mutateAsync } = useMutation({
    mutationFn: createLesson,
  });

  const { data: assessments = [], isPending: isLoadingAllAssessments } = useQuery(getAssessments());
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const onSubmit = ({ formData, closeModal, reset }: SubmitProps<z.infer<typeof formSchema>>) => {
    const { overridenAssessmentContents, ...others } = formData;
    delete others.selectedAssessmentIdForContentsOverride; // temporary field used for UI only. Do not send to server
    const assessmentContentSelectionOverride: CreateLessonOptions['assessmentContentSelectionOverride'] = assessments
      .map((assessment, index) => {
        const { useContentsOverride, overridenContents } = overridenAssessmentContents[index];
        if (!formData.assessmentIds.includes(assessment.id) || !useContentsOverride) return null;

        return overridenContents.map((content) => ({
          assessmentId: assessment.id,
          assessmentContentId: content.value,
        }));
      })
      .filter(isNotNullish)
      .flat();

    return mutateAsync({
      ...others,
      handouts: textAreaValueToArray(formData.handouts),
      objectives: textAreaValueToArray(formData.objectives),
      assessmentContentSelectionOverride,
      lessonLinkKTo2: formData.lessonLinkKTo2 || null,
      lessonLink3To5: formData.lessonLink3To5 || null,
      lessonLink6To8: formData.lessonLink6To8 || null,
      minWPM: formData.minWPM ?? null,
      maxWPM: formData.maxWPM ?? null,
      minAccuracyPercent: formData.minAccuracyPercent ?? null,
      maxAccuracyPercent: formData.maxAccuracyPercent ?? null,
      shouldSkipFeedback: null, // Does not allow user to set this field
      floridaStandard: formData.floridaStandard || null,
      arkansasStandard: formData.arkansasStandard || null,
    })
      .then(() => {
        toast({
          title: 'Lesson created successfully',
        });
        closeModal();
        reset();
        queryClient.invalidateQueries(getLessons());
      })
      .catch(() => {
        toast({
          title: 'Lesson creation failed',
        });
      });
  };

  if (isLoadingAllAssessments) {
    return <Button loading>Create Lesson</Button>;
  }

  return (
    <FormModal
      modalTitle="Create Lesson"
      formSchema={formSchema}
      onSubmit={onSubmit}
      trigger={<Button>Create Lesson</Button>}
      dialogContentProps={{ className: 'max-w-3xl' }}
      defaultValues={{
        assessmentIds: [],
        overridenAssessmentContents: Array(assessments.length)
          .fill(0)
          .map(() => ({
            useContentsOverride: false,
            overridenContents: [],
          })),
      }}
    >
      {({ form }) => {
        const { selectedAssessmentIdForContentsOverride, assessmentIds, overridenAssessmentContents } = form.watch();
        const assessmentSelectingContents = assessments.find(
          ({ id }) => id === selectedAssessmentIdForContentsOverride && assessmentIds.includes(id),
        );
        const contentOptions: MultiSelectOption[] =
          assessmentSelectingContents?.contents
            .filter(({ format }) => allowedForContentsOverride(format))
            .map(buildNameLikeSelectOptions) || [];
        const selectedAssessmentIndex = assessments.findIndex(
          ({ id }) => id === selectedAssessmentIdForContentsOverride,
        );

        return (
          <div className="grid gap-4 py-4">
            <FormInputField form={form} name="name" label="Lesson name" />
            <FormInputField
              form={form}
              name="lessonLinkKTo2"
              label="Lesson link for K-2"
              inputProps={{ placeholder: 'Lesson slides link for K-2 url' }}
            />
            <FormInputField
              form={form}
              name="lessonLink3To5"
              label="Lesson link for 3-5"
              inputProps={{ placeholder: 'Lesson slides link for 3-5 url' }}
            />
            <FormInputField
              form={form}
              name="lessonLink6To8"
              label="Lesson link for 6-8"
              inputProps={{ placeholder: 'Lesson slides link for 6-8 url' }}
            />
            <FormTextArea
              form={form}
              name="handouts"
              label="Handouts (one link per line)"
              textAreaProps={{ placeholder: 'Handouts' }}
            />
            <FormTextArea form={form} name="objectives" label="Learning objectives (one per line)" />
            <div className="grid grid-cols-2 gap-4">
              <FormInputField
                form={form}
                name="minWPM"
                label="# Min WPM"
                inputProps={{ type: 'number', min: 0, step: 1, placeholder: 'e.g. 80' }}
              />
              <FormInputField
                form={form}
                name="maxWPM"
                label="# Max WPM"
                inputProps={{ type: 'number', min: 0, step: 1, placeholder: 'e.g. 120' }}
              />
              <FormInputField
                form={form}
                name="minAccuracyPercent"
                label="Min Accuracy (%)"
                inputProps={{ type: 'number', min: 0, step: 0.01, placeholder: 'e.g. 35.13' }}
              />
              <FormInputField
                form={form}
                name="maxAccuracyPercent"
                label="Max Accuracy (%)"
                inputProps={{ type: 'number', min: 0, step: 0.01, placeholder: 'e.g. 80.5' }}
              />
            </div>
            <div className="flex align-center">
              <div className="basis-1/3">
                <FormCheckboxGroup
                  label="Assessments"
                  form={form}
                  name="assessmentIds"
                  options={assessments.map(({ id }) => id)}
                  optionLabel={(id) => {
                    const assessment = assessments.find(({ id: assessmentId }) => assessmentId === id);
                    if (!assessment) return null;
                    const canPickSpecificContents = assessment.contents.some(({ format }) =>
                      allowedForContentsOverride(format),
                    );

                    return (
                      <div className="flex gap-x-1 items-center">
                        {assessment.name}
                        {canPickSpecificContents && assessmentIds.includes(id) && (
                          <Button
                            type="button"
                            size={'sm'}
                            variant={'link'}
                            onClick={() => form.setValue('selectedAssessmentIdForContentsOverride', id)}
                          >
                            Pick specific contents
                          </Button>
                        )}
                      </div>
                    );
                  }}
                />
              </div>
              {assessmentSelectingContents && selectedAssessmentIndex !== -1 && (
                <div className="flex flex-col gap-y-1 basis-2/3" key={selectedAssessmentIndex}>
                  <FormCheckbox
                    label={`Include only selected assessment contents for ${assessmentSelectingContents.name}`}
                    form={form}
                    name={`overridenAssessmentContents.${selectedAssessmentIndex}.useContentsOverride`}
                  />
                  <FormMultiSelect
                    form={form}
                    label={'Assessment contents to include'}
                    name={`overridenAssessmentContents.${selectedAssessmentIndex}.overridenContents`}
                    multipleSelectorProps={{
                      options: contentOptions,
                      disabled: !overridenAssessmentContents[selectedAssessmentIndex].useContentsOverride,
                      placeholder: `Select which assessment contents to run for this lesson`,
                    }}
                  />
                </div>
              )}
            </div>
            <FormTextArea form={form} name="floridaStandard" label="Florida Standard" />
            <FormTextArea form={form} name="arkansasStandard" label="Arkansas Standard" />
          </div>
        );
      }}
    </FormModal>
  );
};
